import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../services/axios';
import { useAuth } from '../../context/AuthContext';
import { buildJdPreviewNavigationState } from '../../utils/jdPreviewNavigation';
import { formatAppDate, formatAppDateFromKey } from '../../utils/dateFormat';
import { formatPositionStatusLabel, positionStatusKey } from '../../utils/positionStatus';
import NewPositionModal from './NewPositionModal';
import { usePermissions } from '../../context/PermissionsContext';
import { PositionBadges, PositionAccessMenu, AssignPositionModal, TransferPositionModal } from './PositionAccessControls';

// ── Helpers ────────────────────────────────────────────────────────────────
const CLIENT_COLORS = ['#0041C2', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4', '#EF4444'];
const getCC = (name) => {
  const s = name || '';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return CLIENT_COLORS[Math.abs(h) % CLIENT_COLORS.length];
};
const getIni = (name) => {
  const s = name || '';
  return s.split(/\s+/).filter(Boolean).map(w => w[0]).join('').substring(0, 2).toUpperCase();
};

// Map our DB statuses → reference badge classes + labels
const STATUS_MAP = {
  Pending: { cls: 'ds-st-pending', label: 'Pending' },
  Stacked: { cls: 'ds-st-active',  label: 'Stacked' },
  Close:   { cls: 'ds-st-closed',  label: 'Closed'  },
  Closed:  { cls: 'ds-st-closed',  label: 'Closed'  },
};
const getSC = (s) => STATUS_MAP[s] || STATUS_MAP.Pending;

const passesStatusFilter = (position, statusFilter) => {
  if (!statusFilter) return true;
  return positionStatusKey(position) === statusFilter;
};

/** Same rules as the grid: search, status filter, date, and optional analytics focus IDs. */
const applyPositionGridFilters = (data, { statusFilter, dateFilter, searchTerm, focusPositionIds }) => {
  let result = Array.isArray(data) ? [...data] : [];
  if (focusPositionIds instanceof Set && focusPositionIds.size > 0) {
    result = result.filter(p => focusPositionIds.has(String(p.positionId)));
  }
  if (statusFilter) {
    result = result.filter(p => passesStatusFilter(p, statusFilter));
  }
  if (dateFilter && !isFutureLocalDateKey(dateFilter)) {
    result = result.filter(p => {
      const d = new Date(p.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return key === dateFilter;
    });
  }
  const q = (searchTerm || '').trim().toLowerCase();
  if (q) {
    result = result.filter(p =>
      (p.positionName || '').toLowerCase().includes(q) ||
      (p.clientName || '').toLowerCase().includes(q) ||
      (p.jobCode || '').toLowerCase().includes(q)
    );
  }
  return result;
};

const toLocalDateKey = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const isFutureLocalDateKey = (dateKey) => {
  if (!dateKey || !/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return false;
  return dateKey > toLocalDateKey();
};

/** Allow empty, today, or past local dates only. */
const normalizeCreatedDateFilter = (value) => {
  const v = (value || '').trim();
  if (!v) return '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v) || isFutureLocalDateKey(v)) return '';
  return v;
};

// ── Component-scoped CSS (uses design-system tokens from globals.css) ──────
const STYLES = `
.pt-wrap{font-family:'Plus Jakarta Sans',sans-serif;color:var(--ds-text);width:100%;max-width:100%;box-sizing:border-box;}

/* ── Tabs ── */
.pt-tabs{display:flex;gap:4px;margin-bottom:14px;flex-wrap:wrap;}
.pt-tab{padding:6px 14px;border-radius:7px;font-size:11px;font-weight:600;cursor:pointer;transition:all 0.18s;border:1px solid transparent;color:var(--ds-muted);background:transparent;}
.pt-tab:hover:not(.on){color:var(--ds-text2);}
.pt-tab.on{background:var(--ds-active);border-color:var(--ds-border2);color:#1A5FD4;}

/* ── Toolbar ── */
.pt-search{display:flex;align-items:center;gap:8px;background:var(--ds-input-bg);border:1px solid var(--ds-border);border-radius:9px;padding:8px 14px;min-width:240px;transition:all 0.18s;box-shadow:var(--ds-shadow);}
.pt-search:focus-within{border-color:#0041C2;box-shadow:0 0 0 3px rgba(0,65,194,0.12);}
.pt-search input{background:transparent;border:none;outline:none;font-size:12px;font-family:'Plus Jakarta Sans',sans-serif;color:var(--ds-text);width:100%;}
.pt-search input::placeholder{color:var(--ds-muted);}
.pt-sel{background:var(--ds-input-bg);border:1px solid var(--ds-border);border-radius:9px;padding:8px 30px 8px 12px;font-size:12px;font-family:'Plus Jakarta Sans',sans-serif;color:var(--ds-text);outline:none;appearance:none;cursor:pointer;box-shadow:var(--ds-shadow);transition:all 0.18s;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%235E6A8A' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat:no-repeat;background-position:right 9px center;}
.pt-sel:focus{border-color:#0041C2;box-shadow:0 0 0 3px rgba(0,65,194,0.1);}
.pt-sel option{background:var(--ds-card);color:var(--ds-text);}
.pt-ibtn{display:flex;align-items:center;gap:6px;padding:8px 14px;border-radius:9px;font-size:12px;font-family:'Plus Jakarta Sans',sans-serif;font-weight:500;cursor:pointer;transition:all 0.18s;border:1px solid var(--ds-border2);background:transparent;color:var(--ds-text2);}
.pt-ibtn:hover{border-color:#1A5FD4;color:#1A5FD4;background:var(--ds-hover);}
.pt-ibtn.primary{background:linear-gradient(135deg,#0041C2,#1A5FD4);color:#fff;border-color:transparent;font-weight:600;box-shadow:0 0 14px rgba(0,65,194,0.3);}
.pt-ibtn.primary:hover{box-shadow:0 0 36px rgba(0,65,194,0.5);transform:translateY(-1px);}

/* ── Table panel ── */
.pt-panel{background:var(--ds-card);border:1px solid var(--ds-border);border-radius:14px;overflow:hidden;box-shadow:var(--ds-shadow);}
.pt-table-wrap{width:100%;max-width:100%;overflow-x:auto;overflow-y:visible;-webkit-overflow-scrolling:touch;}
.pt-table-wrap::-webkit-scrollbar{height:10px;}
.pt-table-wrap::-webkit-scrollbar-track{background:var(--ds-surface);border-radius:5px;margin:0 4px;}
.pt-table-wrap::-webkit-scrollbar-thumb{background:var(--ds-border2);border-radius:5px;border:2px solid var(--ds-surface);}
.pt-table-wrap::-webkit-scrollbar-thumb:hover{background:#1A5FD4;}
.pt-table-craft{width:100%;min-width:100%;max-width:100%;table-layout:fixed;box-sizing:border-box;}
.pt-table-craft col.pt-col-job-role{width:200px;}
.pt-table-craft thead th,.pt-table-craft tbody td{vertical-align:middle;box-sizing:border-box;}
.pt-table-craft thead th:nth-child(2),.pt-table-craft tbody td:nth-child(2){white-space:normal;word-wrap:break-word;overflow-wrap:break-word;}
.pt-table.pt-table-craft tbody td:nth-child(2){vertical-align:top;}
.pt-table-craft .pt-role-cell,.pt-table-craft .pt-client-cell,.pt-table-craft .pt-created-by{overflow:visible;}
.pt-table-craft .pt-role-cell{min-width:0;max-width:100%;align-items:flex-start;}
.pt-table-craft .pt-role-cell .pt-role-name{flex:1 1 auto;min-width:0;max-width:100%;white-space:normal;overflow-wrap:break-word;word-break:break-word;}
.pt-table-craft .pt-client-cell .pt-client-name{flex:1 1 auto;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0;}
.pt-table-craft .pt-col-sno{width:3.5rem;min-width:3.5rem;max-width:3.5rem;}
.pt-table-stack{width:100%;max-width:100%;table-layout:fixed;box-sizing:border-box;}
.pt-table-stack thead th,.pt-table-stack tbody td{padding:8px 6px;overflow:hidden;vertical-align:middle;box-sizing:border-box;}
.pt-table-stack thead th{font-size:0.5625rem;letter-spacing:0.06em;}
.pt-table-stack tbody td{font-size:0.75rem;padding:8px 6px;}
.pt-table-stack thead th{white-space:nowrap;line-height:1.3;overflow:hidden;text-overflow:ellipsis;}
.pt-table-stack thead th.pt-th-ranked,.pt-table-stack thead th.pt-th-status{overflow:visible;text-overflow:clip;}
.pt-ellipsis{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%;min-width:0;}
.pt-table-stack .pt-client-cell,.pt-table-stack .pt-created-by{min-width:0;max-width:100%;overflow:hidden;}
.pt-table-stack .pt-role-cell{min-width:0;max-width:100%;overflow:visible;align-items:flex-start;gap:0;}
.pt-table-stack .pt-role-ico{display:none;}
.pt-table-stack .pt-role-name{font-size:12px;white-space:normal;overflow-wrap:break-word;word-break:break-word;min-width:0;max-width:100%;}
.pt-table-stack tbody td:nth-child(2){vertical-align:top;overflow:visible;}
.pt-table-stack .pt-client-av{width:22px;height:22px;}
.pt-table-stack .pt-client-name{font-size:11px;}
.pt-table-stack .pt-code{padding:2px 6px;font-size:10px;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:inline-block;vertical-align:middle;}
.pt-table-stack .pt-date{font-size:10px;}
.pt-table-stack .pt-cell-status{overflow:hidden;text-align:left;}
.pt-table-stack .pt-badge{display:inline-flex;align-items:center;padding:0.2rem 0.45rem;font-size:0.625rem;gap:0.25rem;width:100%;max-width:100%;box-sizing:border-box;overflow:hidden;min-width:0;line-height:1.25;}
.pt-table-stack .pt-badge-label{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0;flex:1 1 auto;}
.pt-table-stack .pt-badge-dot{flex-shrink:0;width:0.3125rem;height:0.3125rem;}
.pt-table-stack .pt-col-actions{white-space:nowrap;text-align:center;padding-left:6px!important;padding-right:6px!important;}
.pt-table-stack .pt-act-btns{gap:3px;flex-wrap:nowrap;justify-content:center;}
.pt-table-stack .pt-act{width:1.625rem;height:1.625rem;min-width:1.625rem;border-radius:6px;flex-shrink:0;}
.pt-table-stack .pt-act svg{width:0.75rem;height:0.75rem;}
.pt-table-stack .pt-sort-ico{margin-left:3px;}
.pt-table-stack thead th.pt-th-created .pt-sort-ico{margin-left:2px;}
.pt-table-stack .pt-col-sno{width:3rem;min-width:3rem;max-width:3rem;text-align:center;padding-left:0.35rem;padding-right:0.35rem;overflow:visible;text-overflow:clip;}
.pt-table-stack .pt-sno-val{display:inline-block;font-weight:800;font-variant-numeric:tabular-nums;white-space:nowrap;line-height:1.2;}
.pt-table-stack thead th.pt-col-sno{overflow:visible;text-overflow:clip;}
.pt-panel-head{padding:16px 20px;border-bottom:1px solid var(--ds-border);display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px;}
.pt-panel-head h2{font-size:14px;font-weight:700;color:var(--ds-text);margin:0;}
.pt-panel-head p{font-size:11px;color:var(--ds-muted);margin:3px 0 0;}
.pt-results{font-size:11px;color:var(--ds-muted);background:var(--ds-surface);border:1px solid var(--ds-border);padding:3px 10px;border-radius:20px;font-weight:500;}

/* ── Table ── */
.pt-table{width:100%;border-collapse:collapse;}
.pt-table thead tr{border-bottom:1px solid var(--ds-table-border);background:var(--ds-surface);}
.pt-table thead th{padding:11px 16px;text-align:left;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:var(--ds-muted);font-weight:600;white-space:nowrap;user-select:none;cursor:pointer;transition:color 0.15s;}
.pt-table thead th:hover{color:#1A5FD4;}
.pt-table thead th.sorted{color:#1A5FD4;}
.pt-sort-ico{display:inline-flex;flex-direction:column;gap:1.5px;margin-left:5px;vertical-align:middle;opacity:0.35;}
.pt-table thead th.sorted .pt-sort-ico{opacity:1;}
.pt-sort-ico svg{width:7px;height:7px;fill:currentColor;}
.pt-table tbody tr{border-bottom:1px solid var(--ds-table-border);transition:background 0.18s,box-shadow 0.18s;}
.pt-table tbody tr:last-child{border-bottom:none;}
.pt-table tbody tr:hover{background:var(--ds-table-hover);}
.pt-table tbody tr.click{cursor:pointer;}
.pt-table tbody tr.click:hover{background:rgba(0,65,194,0.06);box-shadow:0 2px 12px rgba(0,65,194,0.13);}
.pt-table tbody tr.click:hover td:first-child{box-shadow:inset 3px 0 0 #1A5FD4;}
.pt-table tbody tr.click:hover .pt-role-name{color:#1A5FD4;}
/* OLD: .pt-table tbody td{padding:13px 16px;font-size:13px;color:var(--ds-text);vertical-align:middle;transition:box-shadow 0.18s;} */
.pt-table tbody td{padding:13px 16px;font-size:13px;color:var(--ds-text);font-weight:500;vertical-align:middle;transition:box-shadow 0.18s;}

/* Craft JD — left-aligned headers and cells under each column */
.pt-table.pt-table-craft thead th,.pt-table.pt-table-craft tbody td{text-align:left;vertical-align:middle;}
.pt-table.pt-table-craft .pt-role-cell,.pt-table.pt-table-craft .pt-client-cell,.pt-table.pt-table-craft .pt-created-by{justify-content:flex-start;}
.pt-table.pt-table-craft thead th.pt-th-created-on{cursor:default;overflow:visible;white-space:nowrap;}
.pt-th-created-on-head{display:flex;align-items:center;gap:5px;line-height:1;min-height:20px;}
.pt-th-created-on-label,.pt-th-created-on-sort{display:inline-flex;align-items:center;cursor:pointer;flex-shrink:0;}
.pt-th-created-on-label:hover,.pt-th-created-on-sort:hover{color:#1A5FD4;}
.pt-th-created-on .pt-sort-ico{margin-left:0;vertical-align:middle;}
.pt-th-date-filter{position:relative;display:inline-flex;align-items:center;flex-shrink:0;margin-left:1px;}
.pt-th-date-filter-btn{display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;padding:0;margin:0;background:transparent;border:1px solid var(--ds-border);border-radius:5px;cursor:pointer;color:var(--ds-muted);line-height:0;transition:all 0.15s;box-sizing:border-box;}
.pt-th-date-filter-btn.is-active,.pt-th-date-filter-btn:hover{border-color:rgba(0,65,194,0.5);color:#4D86E8;background:rgba(0,65,194,0.2);}

/* ── Cell types ── */
.pt-role-cell{display:flex;align-items:center;gap:10px;}
.pt-role-ico{width:32px;height:32px;border-radius:8px;flex-shrink:0;display:flex;align-items:center;justify-content:center;background:rgba(0,65,194,0.1);border:1px solid rgba(0,65,194,0.15);transition:all 0.2s;}
.pt-table tbody tr.click:hover .pt-role-ico{background:rgba(0,65,194,0.22);border-color:rgba(0,65,194,0.5);transform:scale(1.12);box-shadow:0 0 10px rgba(0,65,194,0.2);}
.pt-role-ico svg{width:14px;height:14px;stroke:#4D86E8;stroke-width:1.7;}
/* OLD: .pt-role-name{font-weight:600;color:var(--ds-text2);font-size:13px;line-height:1.3;transition:color 0.18s;} */
.pt-role-name{font-weight:700;color:var(--ds-text);font-size:13px;line-height:1.4;transition:color 0.18s;white-space:normal;overflow-wrap:break-word;word-break:break-word;min-width:0;}
.pt-client-cell{display:flex;align-items:center;gap:8px;}
.pt-client-av{width:26px;height:26px;border-radius:7px;flex-shrink:0;display:flex;align-items:center;justify-content:center;}
.pt-client-av span{font-size:9px;font-weight:700;}
/* OLD: .pt-client-name{font-size:12px;font-weight:500;color:var(--ds-text2);} */
.pt-client-name{font-size:12px;font-weight:600;color:var(--ds-text);}
.pt-code{display:inline-flex;align-items:center;background:var(--ds-code-bg);border:1px solid var(--ds-code-border);border-radius:6px;padding:3px 9px;font-size:11px;font-weight:600;color:var(--ds-code-text);font-family:'Courier New',monospace;letter-spacing:0.04em;}
/* OLD: .pt-date{font-size:11px;color:var(--ds-muted);font-family:monospace;} */
.pt-date{font-size:11px;color:var(--ds-text2);font-family:monospace;font-weight:500;}

/* ── Status badges ── */
.pt-badge{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:20px;font-size:11px;font-weight:600;white-space:nowrap;border:1px solid transparent;}
.pt-badge-dot{width:5px;height:5px;border-radius:50%;flex-shrink:0;}
.ds-st-active{background:var(--ds-badge-active-bg);color:var(--ds-badge-active-text);border-color:var(--ds-badge-active-border);}
.ds-st-active .pt-badge-dot{background:var(--ds-badge-active-text);}
.ds-st-pending{background:var(--ds-badge-pending-bg);color:var(--ds-badge-pending-text);border-color:var(--ds-badge-pending-border);}
.ds-st-pending .pt-badge-dot{background:var(--ds-badge-pending-text);}
.ds-st-review{background:var(--ds-badge-review-bg);color:var(--ds-badge-review-text);border-color:var(--ds-badge-review-border);}
.ds-st-review .pt-badge-dot{background:var(--ds-badge-review-text);}
.ds-st-closed{background:var(--ds-badge-closed-bg);color:var(--ds-badge-closed-text);border-color:var(--ds-badge-closed-border);}
.ds-st-closed .pt-badge-dot{background:var(--ds-badge-closed-text);}

/* ── Action buttons ── */
.pt-act-btns{display:flex;align-items:center;gap:4px;justify-content:center;}
.pt-act{width:28px;height:28px;border-radius:7px;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all 0.18s;border:1px solid var(--ds-border);background:transparent;}
.pt-act:disabled{opacity:0.4;cursor:not-allowed;}
.pt-act svg{width:13px;height:13px;stroke:var(--ds-muted);stroke-width:1.7;transition:stroke 0.15s;}
.pt-act:hover:not(:disabled){border-color:#1A5FD4;background:var(--ds-hover);}
.pt-act:hover:not(:disabled) svg{stroke:#4D86E8;}
.pt-act.run:hover:not(:disabled){border-color:#10B981;background:rgba(16,185,129,0.08);}
.pt-act.run:hover:not(:disabled) svg{stroke:#10B981;}
.pt-act.close:hover:not(:disabled){border-color:#f59e0b;background:rgba(245,158,11,0.1);}
.pt-act.close:hover:not(:disabled) svg{stroke:#f59e0b;}
.pt-act.del:hover:not(:disabled){border-color:#EF4444;background:rgba(239,68,68,0.08);}
.pt-act.del:hover:not(:disabled) svg{stroke:#EF4444;}

/* ── Pagination ── */
.pt-pg-wrap{display:flex;align-items:center;justify-content:space-between;padding:13px 20px;border-top:1px solid var(--ds-border);flex-wrap:wrap;gap:10px;}
.pt-pg-info{font-size:12px;color:var(--ds-muted);}
.pt-pg-btns{display:flex;align-items:center;gap:4px;}
.pt-pg{min-width:30px;height:30px;padding:0 6px;border-radius:7px;display:flex;align-items:center;justify-content:center;border:1px solid var(--ds-border);background:transparent;font-size:12px;font-weight:600;color:var(--ds-text2);cursor:pointer;transition:all 0.15s;font-family:'Plus Jakarta Sans',sans-serif;}
.pt-pg:hover:not(:disabled){border-color:#1A5FD4;color:#1A5FD4;background:var(--ds-hover);}
.pt-pg:disabled{opacity:0.3;cursor:not-allowed;}
.pt-pg.on{background:linear-gradient(135deg,#0041C2,#1A5FD4);color:#fff;border-color:transparent;box-shadow:0 0 10px rgba(0,65,194,0.3);}
.pt-pg-sep{font-size:12px;color:var(--ds-muted);padding:0 4px;}

/* ── Date popover ── */
.pt-datepop{position:absolute;top:calc(100% + 6px);right:0;left:auto;z-index:9999;background:var(--ds-card);border:1px solid var(--ds-border2);border-radius:10px;padding:12px 14px;box-shadow:var(--ds-shadow-md);min-width:340px;width:max-content;max-width:380px;box-sizing:border-box;}
.pt-datepop label{font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:var(--ds-muted);font-weight:600;display:block;margin:0 0 10px;}
.pt-datepop-fields{display:flex;flex-direction:column;align-items:stretch;gap:6px;width:100%;}
.pt-datepop-row{display:flex;align-items:center;gap:8px;width:100%;}
.pt-datepop input[type=date]{flex:1;min-width:0;box-sizing:border-box;background:var(--ds-input-bg);border:1px solid var(--ds-border2);border-radius:6px;padding:7px 9px;font-size:12px;outline:none;color:var(--ds-text);font-family:'Plus Jakarta Sans',sans-serif;height:34px;}
.pt-datepop-selected{font-size:11px;color:var(--ds-text2);padding:0 2px;}
[data-theme="dark"] .pt-datepop input[type=date]{color-scheme:dark;}
[data-theme="light"] .pt-datepop input[type=date]{color-scheme:light;}
.pt-datepop .pt-clr{margin:0;flex-shrink:0;box-sizing:border-box;background:var(--ds-hover);border:1px solid var(--ds-border2);border-radius:6px;padding:0 10px;height:34px;font-size:11px;font-weight:600;cursor:pointer;color:#1A5FD4;font-family:'Plus Jakarta Sans',sans-serif;transition:all 0.15s;white-space:nowrap;line-height:1;display:inline-flex;align-items:center;justify-content:center;}
.pt-datepop .pt-clr:hover:not(:disabled){background:var(--ds-active);}
.pt-datepop .pt-clr:disabled{opacity:0.45;cursor:not-allowed;color:var(--ds-muted);}

/* ── Page heading ── */
.pt-page-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-bottom:20px;}
.pt-page-title{font-size:22px;font-weight:800;color:var(--ds-text);margin:0;line-height:1.2;}
.pt-page-tag{font-size:13px;color:var(--ds-text2);margin:4px 0 0;line-height:1.5;}

/* ── Stat boxes ── */
.pt-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:20px;}
@media(max-width:900px){.pt-stats{grid-template-columns:repeat(2,1fr);}}
@media(max-width:480px){.pt-stats{grid-template-columns:1fr;}}
.pt-stat{background:var(--ds-card);border:1px solid var(--ds-border);border-radius:12px;padding:18px 20px;overflow:hidden;box-shadow:var(--ds-shadow);position:relative;}
.pt-stat-bar{position:absolute;top:0;left:0;right:0;height:3px;border-radius:12px 12px 0 0;}
.pt-stat-lbl{font-size:10px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:var(--ds-muted);margin-bottom:10px;}
.pt-stat-val{font-size:30px;font-weight:800;line-height:1;margin-bottom:6px;}
.pt-stat-sub{display:flex;align-items:center;gap:5px;font-size:11px;color:var(--ds-text2);}
.pt-stat-dot{width:6px;height:6px;border-radius:50%;flex-shrink:0;}

/* ── Empty state ── */
.pt-empty{padding:52px 20px;text-align:center;}
.pt-empty-ico{width:48px;height:48px;border-radius:12px;margin:0 auto 12px;background:var(--ds-surface);border:1px solid var(--ds-border);display:flex;align-items:center;justify-content:center;}
.pt-empty-ico svg{width:20px;height:20px;stroke:var(--ds-muted);}
.pt-empty p{font-size:13px;color:var(--ds-muted);line-height:1.7;}

/* ── Split panel ── */
.spl-item{background:var(--ds-surface);border:1px solid var(--ds-border);border-left:3px solid transparent;border-radius:8px;margin-bottom:6px;transition:all 0.2s;cursor:pointer;padding:10px 11px;width:100%;text-align:left;color:var(--ds-text);}
.spl-item:hover{background:var(--ds-active);border-color:#0041C2;border-left-color:#1A5FD4;box-shadow:0 4px 16px rgba(0,65,194,0.18);transform:translateX(3px);}
.spl-item:hover .spl-chevron{stroke:#1A5FD4;transform:translateX(2px);}
.spl-item.sel{background:var(--ds-active);border-color:#0041C2;border-left-color:#0041C2;}
.spl-chevron{transition:transform 0.18s,stroke 0.18s;}
.spl-search{background:var(--ds-input-bg);border:1px solid var(--ds-border);border-radius:8px;padding:8px 12px 8px 30px;font-size:12px;width:100%;outline:none;color:var(--ds-text);font-family:'Plus Jakarta Sans',sans-serif;transition:border-color 0.18s;}
.spl-search:focus{border-color:#0041C2;}
.spl-search::placeholder{color:var(--ds-muted);}

/* transitions */
.pt-panel,.pt-search,.pt-sel,.pt-ibtn,.pt-badge,.pt-act,.pt-pg,.pt-code,.pt-tab{transition:background 0.2s,border-color 0.2s,color 0.2s,box-shadow 0.2s;}
`;

export default function PositionsTable({ mode = 'craftJD', onEnterWizard }) {
  const { user, tenantId, isLoading: authLoading } = useAuth();
  const perms = usePermissions();
  const [assignTarget, setAssignTarget] = useState(null);
  const [transferTarget, setTransferTarget] = useState(null);
  const [onlyMine, setOnlyMine] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isCraftJD = mode === 'craftJD';
  const craftColCount = 8;

  // ── Data ──────────────────────────────────────────────────────────────────
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resumeCounts, setResumeCounts] = useState({});
  const [jdVersions, setJdVersions] = useState({});
  // ── Filters + sort ────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  /** Optional focus from JD Analytics funnel → Craft JD deep link */
  const [analyticsFocusIds, setAnalyticsFocusIds] = useState(null);
  const [analyticsFocusLabel, setAnalyticsFocusLabel] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 8;

  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef(null);
  const [maxCreatedDate, setMaxCreatedDate] = useState(() => toLocalDateKey());

  useEffect(() => {
    if (showDatePicker) setMaxCreatedDate(toLocalDateKey());
  }, [showDatePicker]);

  const handleCreatedDateFilterChange = useCallback((value) => {
    const trimmed = (value || '').trim();
    const next = normalizeCreatedDateFilter(value);
    if (trimmed && !next) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed) && isFutureLocalDateKey(trimmed)) {
        toast.info('Created date cannot be in the future.');
      }
      return;
    }
    setDateFilter(next);
    setCurrentPage(1);
  }, []);

  // ── craftJD split panel ───────────────────────────────────────────────────
  const [layoutMode, setLayoutMode] = useState('table');
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [loadingResumes, setLoadingResumes] = useState(false);
  const [splitSearch, setSplitSearch] = useState('');
  const [splitPage, setSplitPage] = useState(1);
  const SPLIT_PAGE_SIZE = 8;

  /* Honor JD Analytics → Craft JD navigation (filter to selected position IDs). */
  useEffect(() => {
    const state = location.state;
    if (!state?.fromJdAnalytics) return;
    const ids = Array.isArray(state.analyticsFocusPositionIds)
      ? state.analyticsFocusPositionIds.map(String).filter(Boolean)
      : [];
    if (ids.length > 0) {
      setAnalyticsFocusIds(new Set(ids));
      setAnalyticsFocusLabel(state.analyticsFocusLabel || `${ids.length} from JD Analytics`);
      setCurrentPage(1);
      setLayoutMode('table');
    } else {
      setAnalyticsFocusIds(null);
      setAnalyticsFocusLabel('');
    }
  }, [location.state]);

  const clearAnalyticsFocus = useCallback(() => {
    setAnalyticsFocusIds(null);
    setAnalyticsFocusLabel('');
    setCurrentPage(1);
    if (location.state?.fromJdAnalytics) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  // ── Delete modal ──────────────────────────────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingPosition, setDeletingPosition] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ── Close position modal ──────────────────────────────────────────────────
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closingPosition, setClosingPosition] = useState(null);
  const [closeLoading, setCloseLoading] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const CLOSE_REASON_MAX = 500;
  const CLOSE_REASON_PRESETS = ['Role filled', 'On hold', 'Cancelled by client', 'Duplicate posting'];

  // ── New Position modal ────────────────────────────────────────────────────
  const [showNewPositionModal, setShowNewPositionModal] = useState(false);
  const [newPositionSource, setNewPositionSource]       = useState('manual');
  const [showSourceDropdown, setShowSourceDropdown]     = useState(false);
  const sourceDropdownRef = useRef(null);
  const [currentUser, setCurrentUser]   = useState(null);
  const [createdByMap, setCreatedByMap] = useState({});

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchPositions = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/positions/with-counts', {
        params: { _: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      setPositions(data);
      const counts = {};
      const versions = {};
      data.forEach(p => {
        counts[p.positionId] = p.resumeCount ?? 0;
        versions[p.positionId] = p.jdCount > 0 ? [{ stub: true }] : [];
      });
      setResumeCounts(counts);
      setJdVersions(versions);
    } catch {
      toast.error('Failed to load positions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    setPositions([]);
    fetchPositions();
  }, [fetchPositions, authLoading, user?.email, tenantId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, dateFilter, searchTerm]);

  useEffect(() => {
    const h = e => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) setShowDatePicker(false);
      if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(e.target)) setShowSourceDropdown(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    api.get('/auth/me').then(res => setCurrentUser(res.data)).catch(() => {});
  }, []);

  // ── Tab click ─────────────────────────────────────────────────────────────
  const handleStatusFilterClick = (filterVal) => {
    const next = filterVal === 'all' ? '' : filterVal;
    setStatusFilter(next);
    setSortColumn(null);
    setCurrentPage(1);
  };

  // ── Grid rows: single filtered list for table + export ────────────────────
  const gridRows = useMemo(
    () => applyPositionGridFilters(positions, {
      statusFilter,
      dateFilter,
      searchTerm,
      focusPositionIds: analyticsFocusIds,
    }),
    [positions, statusFilter, dateFilter, searchTerm, analyticsFocusIds]
  );

  // Static S.No: fixed 1-based serial per row in the current filtered list (unchanged by sort/page).
  const staticSnoByPositionId = useMemo(() => {
    const map = new Map();
    gridRows.forEach((p, i) => map.set(p.positionId, i + 1));
    return map;
  }, [gridRows]);

  const getStaticSno = (positionId) => staticSnoByPositionId.get(positionId) ?? '—';

  const sortedPositions = useMemo(() => {
    const pinRank = (p) => (p.pinnedGlobal ? 0 : p.pinnedByMe ? 1 : 2);
    const rows = onlyMine
      ? gridRows.filter((p) => p.myAccess === 'OWNER' || p.myAccess === 'EDITOR' || p.myAccess === 'VIEWER')
      : gridRows;
    if (!sortColumn) return [...rows].sort((a, b) => pinRank(a) - pinRank(b));
    return [...rows].sort((a, b) => {
      if (pinRank(a) !== pinRank(b)) return pinRank(a) - pinRank(b);
      let va, vb;
      switch (sortColumn) {
        case 'sno':
          va = staticSnoByPositionId.get(a.positionId) ?? 0;
          vb = staticSnoByPositionId.get(b.positionId) ?? 0;
          break;
        case 'jobRole':    va = (a.positionName||'').toLowerCase(); vb = (b.positionName||'').toLowerCase(); break;
        case 'clientName': va = (a.clientName||'').toLowerCase(); vb = (b.clientName||'').toLowerCase(); break;
        case 'jobCode':    va = (a.jobCode||'').toLowerCase(); vb = (b.jobCode||'').toLowerCase(); break;
        case 'createdOn':  va = new Date(a.createdAt).getTime(); vb = new Date(b.createdAt).getTime(); break;
        case 'status':     va = positionStatusKey(a); vb = positionStatusKey(b); break;
        default: return 0;
      }
      if (va < vb) return sortDirection === 'asc' ? -1 : 1;
      if (va > vb) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [gridRows, sortColumn, sortDirection, staticSnoByPositionId, onlyMine]);

  const handleSort = col => {
    if (sortColumn === col) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(col); setSortDirection('asc'); }
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(sortedPositions.length / PAGE_SIZE);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const currentRecords = sortedPositions.slice(pageStart, pageStart + PAGE_SIZE);

  // ── Stat counts ───────────────────────────────────────────────────────────
  const statCounts = useMemo(() => ({
    total:   positions.length,
    active:  positions.filter(p => positionStatusKey(p) === 'Stacked').length,
    pending: positions.filter(p => positionStatusKey(p) === 'Pending').length,
    closed:  positions.filter(p => positionStatusKey(p) === 'Close').length,
  }), [positions]);

  // ── Resume fetch ──────────────────────────────────────────────────────────
  const fetchResumes = useCallback(async positionId => {
    setLoadingResumes(true);
    try {
      const res = await api.get(`/resumes/position/${positionId}`);
      setResumes(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Failed to load resumes');
      setResumes([]);
    } finally {
      setLoadingResumes(false);
    }
  }, []);

  const handleRowClick = position => {
    navigate(`/database/${position.positionId}`);
  };

  const handlePositionSelect = position => {
    setSelectedPosition(position);
    fetchResumes(position.positionId);
  };

  // ── Stack ranking helpers ─────────────────────────────────────────────────
  const hasJdForPosition = async positionId => {
    const cached = jdVersions[positionId];
    if (Array.isArray(cached)) return cached.length > 0;
    try {
      const res = await api.get(`/job-descriptions/position/${positionId}/versions`, { timeout: 15000 });
      const v = Array.isArray(res.data) ? res.data : [];
      setJdVersions(prev => ({ ...prev, [positionId]: v }));
      return v.length > 0;
    } catch { return false; }
  };

  const handleViewJD = async positionId => {
    const hasJd = await hasJdForPosition(positionId);
    if (!hasJd) { toast.info('No JD available for this position.'); return; }
    navigate(`/jd-preview/${positionId}`, {
      state: buildJdPreviewNavigationState({ returnTo: { pathname: location.pathname, search: location.search } }),
    });
  };

  const handleStackRanking = async positionId => {
    const position = positions.find(p => p.positionId === positionId);
    if (position?.status === 'Stacked') {
      try {
        const res = await api.get(`/stack-ranking/position/${positionId}/status`);
        if (res.data?.hasStackRanking && (res.data.resultsCount > 0 || res.data.oldRankingsCount > 0)) {
          navigate(`/stack-ranking/${positionId}?step=results`);
          return;
        }
      } catch {}
    }
    navigate(`/stack-ranking/${positionId}`);
  };

  const handleDeletePosition = pos => { setDeletingPosition(pos); setShowDeleteModal(true); };

  const resetCloseModal = () => {
    setShowCloseModal(false);
    setClosingPosition(null);
    setCloseReason('');
  };

  const handleClosePosition = pos => {
    if (positionStatusKey(pos) === 'Close') {
      toast.info('This position is already closed.');
      return;
    }
    setClosingPosition(pos);
    setCloseReason('');
    setShowCloseModal(true);
  };

  const performDelete = async () => {
    if (!deletingPosition) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/positions/${deletingPosition.positionId}`);
      toast.success('Position deleted');
      setShowDeleteModal(false);
      setDeletingPosition(null);
      fetchPositions();
    } catch {
      toast.error('Failed to delete position');
    } finally {
      setDeleteLoading(false);
    }
  };

  const performClose = async () => {
    if (!closingPosition) return;
    const reason = (closeReason || '').trim();
    if (!reason) {
      toast.warning('Please enter a comment or reason before closing this position.');
      return;
    }
    if (reason.length > CLOSE_REASON_MAX) {
      toast.warning(`Close reason cannot exceed ${CLOSE_REASON_MAX} characters.`);
      return;
    }
    setCloseLoading(true);
    try {
      const res = await api.put(`/positions/${closingPosition.positionId}/status`, {
        status: 'Close',
        closeReason: reason,
      });
      const updated = res?.data || {};
      setPositions((prev) => prev.map((p) => (
        p.positionId === closingPosition.positionId
          ? {
              ...p,
              ...updated,
              status: 'Close',
              closeReason: updated.closeReason || reason,
              closedAt: updated.closedAt || new Date().toISOString(),
            }
          : p
      )));
      setStatusFilter('Close');
      resetCloseModal();
      toast.success('Position closed');
      window.dispatchEvent(new CustomEvent('reachout:position-status-changed', {
        detail: { positionId: closingPosition.positionId, status: 'Close' },
      }));
      fetchPositions({ silent: true });
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to close position';
      toast.error(msg);
    } finally {
      setCloseLoading(false);
    }
  };

  const renderActionButtons = (pos, btnStyle) => {
    const isClosed = positionStatusKey(pos) === 'Close';
    return (
      <div className="pt-act-btns">
        <button
          className="pt-act run"
          title={perms.canOn(pos, 'ranking.run') ? 'Run Ranking' : 'You can view rankings but not run them on this position'}
          style={btnStyle}
          onClick={() => handleStackRanking(pos.positionId)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        </button>
        <button className="pt-act" title="View JD" style={btnStyle} onClick={() => handleViewJD(pos.positionId)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        </button>
        <button
          className="pt-act close"
          title={isClosed ? 'Already closed' : perms.canOn(pos, 'position.status') ? 'Close position' : 'You are not allowed to close this position'}
          style={btnStyle}
          disabled={isClosed || !perms.canOn(pos, 'position.status')}
          onClick={() => handleClosePosition(pos)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
        </button>
        <button
          className="pt-act del"
          title={perms.canOn(pos, 'position.delete') ? 'Delete' : 'You are not allowed to delete this position'}
          style={btnStyle}
          disabled={!perms.canOn(pos, 'position.delete')}
          onClick={() => handleDeletePosition(pos)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
        </button>
        <PositionAccessMenu
          position={pos}
          onChanged={() => fetchPositions({ silent: true })}
          onAssign={setAssignTarget}
          onTransfer={setTransferTarget}
        />
      </div>
    );
  };

  const exportCSV = useCallback(() => {
    if (!perms.can('data.export')) {
      toast.warn("You don't have permission to export positions.");
      return;
    }
    // Export the same filtered + sorted rows the grid uses (all pages).
    let rowsToExport = sortedPositions;
    if (statusFilter) {
      rowsToExport = rowsToExport.filter(p => passesStatusFilter(p, statusFilter));
    }
    if (rowsToExport.length === 0) {
      toast.info('No records match the current filters.');
      return;
    }
                    const hdr = 'S.No,Job Role,Client Name,Job Code,Created On,Status,Close Reason,Created By\n';
    const rows = rowsToExport.map((p) =>
      [getStaticSno(p.positionId), `"${p.positionName||''}"`, `"${p.clientName||''}"`, p.jobCode||'', formatAppDate(p.createdAt), positionStatusKey(p),
       `"${(p.closeReason || p.close_reason || '').replace(/"/g, '""')}"`,
       `"${p.createdByName || p.createdBy || createdByMap[p.positionId] || ''}"`].join(',')
    ).join('\n');
    const blobUrl = URL.createObjectURL(new Blob([hdr + rows], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = blobUrl;
    const hasFilter = statusFilter || dateFilter || searchTerm.trim();
    a.download = hasFilter ? 'positions-filtered.csv' : 'positions.csv';
    a.click();
    URL.revokeObjectURL(blobUrl);
    const filterHint = statusFilter ? ` (${statusFilter})` : '';
    toast.success(`Exported ${rowsToExport.length} filtered record${rowsToExport.length !== 1 ? 's' : ''}${filterHint}`);
  }, [sortedPositions, statusFilter, dateFilter, searchTerm, createdByMap, staticSnoByPositionId, perms]);

  const handlePositionCreated = ({ positionId, createdByName }) => {
    if (positionId && createdByName) {
      setCreatedByMap(prev => ({ ...prev, [positionId]: createdByName }));
    }
    fetchPositions();
  };

  // Dropdown trigger for + New Position
  const canCreatePosition = perms.can('position.create');
  const newPositionDropdown = (
    <div ref={sourceDropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="pt-ibtn primary"
        disabled={!canCreatePosition}
        title={canCreatePosition ? undefined : (perms.policyOn('positions.managers_only_create')
          ? 'Only managers can create positions in your company'
          : 'You don\'t have permission to create positions')}
        style={canCreatePosition ? undefined : { opacity: 0.5, cursor: 'not-allowed' }}
        onClick={() => canCreatePosition && setShowSourceDropdown(s => !s)}
      >
        + New Position
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 2 }}>
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>
      {showSourceDropdown && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 5px)', right: 0,
          background: 'var(--ds-card)', border: '1px solid var(--ds-border2)',
          borderRadius: 10, boxShadow: 'var(--ds-shadow-md)', zIndex: 9999,
          minWidth: 220, overflow: 'hidden',
        }}>
          {/* Active options */}
          {[
            { label: '✏  Create Manually', val: 'manual' },
            { label: '⬇  Import from Ceipal', val: 'ceipal' },
            { label: '⬇  Import from Workday',     val: 'workday'     },
            { label: '⬇  Import from Greenhouse',  val: 'greenhouse'  },
          ].filter((opt) => opt.val === 'manual' || perms.can('ats.create_position')).map((opt, i) => (
            <button
              key={opt.val}
              onClick={() => { setNewPositionSource(opt.val); setShowNewPositionModal(true); setShowSourceDropdown(false); }}
              style={{
                display: 'block', width: '100%', padding: '10px 16px',
                background: 'transparent', border: 'none', cursor: 'pointer',
                textAlign: 'left', fontSize: 12, color: 'var(--ds-text)',
                fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500,
                borderTop: i === 0 ? 'none' : '1px solid var(--ds-border)',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--ds-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // ── Sort icon ─────────────────────────────────────────────────────────────
  const sortIco = col => (
    <span className="pt-sort-ico">
      <svg viewBox="0 0 8 8"><path d="M4 1L7 6H1L4 1z"/></svg>
      <svg viewBox="0 0 8 8" style={{ transform:'rotate(180deg)' }}><path d="M4 1L7 6H1L4 1z"/></svg>
    </span>
  );

  // ── Pagination ────────────────────────────────────────────────────────────
  const renderPagination = () => {
    if (sortedPositions.length === 0) return null;
    const start = pageStart + 1;
    const end = Math.min(pageStart + PAGE_SIZE, sortedPositions.length);
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 1) pages.push(i);
      else if (Math.abs(i - currentPage) === 2) pages.push('…');
    }
    const deduped = pages.filter((p, i) => !(p === '…' && pages[i-1] === '…'));
    return (
      <div className="pt-pg-wrap">
        <span className="pt-pg-info">Showing {start}–{end} of {sortedPositions.length}</span>
        <div className="pt-pg-btns">
          <button className="pt-pg" onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage === 1}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          {deduped.map((p, i) =>
            p === '…'
              ? <span key={`d${i}`} className="pt-pg-sep">…</span>
              : <button key={p} className={`pt-pg${p === currentPage ? ' on' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>
          )}
          <button className="pt-pg" onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={currentPage >= totalPages || totalPages === 0}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        </div>
      </div>
    );
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="pt-wrap" style={{ textAlign:'center', padding:'60px 20px' }}>
        <style>{STYLES}</style>
        <Spinner animation="border" style={{ color:'#4D86E8' }}>
          <span className="visually-hidden">Loading…</span>
        </Spinner>
        <p style={{ color:'var(--ds-muted)', marginTop:12, marginBottom:0, fontSize:13 }}>Loading positions…</p>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // craftJD — SPLIT PANEL
  // ══════════════════════════════════════════════════════════════════════════
  if (isCraftJD && layoutMode === 'split') {
    const splFiltered = splitSearch.trim()
      ? positions.filter(p =>
          (p.positionName||'').toLowerCase().includes(splitSearch.toLowerCase()) ||
          (p.jobCode||'').toLowerCase().includes(splitSearch.toLowerCase()))
      : positions;
    const splLast = splitPage * SPLIT_PAGE_SIZE;
    const splFirst = splLast - SPLIT_PAGE_SIZE;
    const splRecords = splFiltered.slice(splFirst, splLast);
    const splTotal = Math.ceil(splFiltered.length / SPLIT_PAGE_SIZE);

    return (
      <div className="pt-wrap">
        <style>{STYLES}</style>

        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#0041C2,#1A5FD4)', borderRadius:'14px 14px 0 0', padding:'12px 20px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:32, height:32, borderRadius:8, background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
            </div>
            <div>
              <h5 style={{ margin:0, color:'white', fontSize:14, fontWeight:700, cursor:'pointer', lineHeight:1.2 }}
                  onClick={() => { setLayoutMode('table'); setSelectedPosition(null); setResumes([]); }}>
                Craft JD
              </h5>
              <p style={{ margin:0, color:'rgba(255,255,255,0.6)', fontSize:'0.72rem' }}>Click title to return · Select a position to view resumes</p>
            </div>
          </div>
          {newPositionDropdown}
        </div>

        {/* Split body */}
        <div style={{ display:'flex', border:'1px solid var(--ds-border)', borderTop:'none', borderRadius:'0 0 14px 14px', overflow:'hidden', background:'var(--ds-card)', minHeight:500 }}>
          {/* Left */}
          <div style={{ width:280, flexShrink:0, borderRight:'1px solid var(--ds-border)', padding:'14px 12px', overflowY:'auto' }}>
            <div style={{ position:'relative', marginBottom:10 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--ds-muted)" strokeWidth="1.8" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input className="spl-search" type="text" value={splitSearch}
                onChange={e => { setSplitSearch(e.target.value); setSplitPage(1); }}
                placeholder="Search positions…" autoComplete="off"/>
            </div>
            {splRecords.length === 0 ? (
              <p style={{ color:'var(--ds-muted)', fontSize:12, textAlign:'center', padding:'20px 0', margin:0 }}>No results.</p>
            ) : splRecords.map(pos => (
              <div key={pos.positionId} className={`spl-item${selectedPosition?.positionId === pos.positionId ? ' sel' : ''}`}
                   style={{ cursor:'pointer' }} onClick={() => handlePositionSelect(pos)}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0 }}>
                    <div style={{ width:26, height:26, borderRadius:6, background:`${getCC(pos.clientName)}18`, border:`1px solid ${getCC(pos.clientName)}35`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <span style={{ fontSize:9, fontWeight:700, color:getCC(pos.clientName) }}>{getIni(pos.clientName)}</span>
                    </div>
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:'var(--ds-text2)', lineHeight:1.3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{pos.positionName}</div>
                      <div style={{ fontSize:10, color:'var(--ds-muted)', marginTop:1 }}>
                        {pos.jobCode && <span style={{ fontWeight:600 }}>{pos.jobCode} · </span>}
                        {formatAppDate(pos.createdAt)}
                      </div>
                    </div>
                  </div>
                  <div style={{ flexShrink:0, marginLeft:4 }} onClick={e => e.stopPropagation()}>
                    {renderActionButtons(pos, { width:22, height:22 })}
                  </div>
                </div>
              </div>
            ))}
            {splTotal > 1 && (
              <div style={{ display:'flex', justifyContent:'center', gap:4, marginTop:10 }}>
                <button className="pt-pg" onClick={() => setSplitPage(p => Math.max(1,p-1))} disabled={splitPage===1} style={{ width:26, height:26 }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
                </button>
                <span style={{ color:'var(--ds-muted)', fontSize:11, padding:'0 6px', lineHeight:'26px' }}>{splitPage}/{splTotal}</span>
                <button className="pt-pg" onClick={() => setSplitPage(p => Math.min(splTotal,p+1))} disabled={splitPage===splTotal} style={{ width:26, height:26 }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
                </button>
              </div>
            )}
          </div>

          {/* Right */}
          <div style={{ flex:1, padding:'16px 20px', overflowY:'auto' }}>
            {!selectedPosition ? (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', minHeight:300, textAlign:'center' }}>
                <div style={{ width:52, height:52, borderRadius:12, background:'var(--ds-hover)', border:'1px solid var(--ds-border2)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:12 }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4D86E8" strokeWidth="1.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </div>
                <h5 style={{ color:'var(--ds-text)', fontWeight:600, margin:'0 0 6px' }}>Select a Position</h5>
                <p style={{ color:'var(--ds-muted)', fontSize:13, margin:0 }}>Choose from the list to view resumes.</p>
              </div>
            ) : (
              <>
                <div style={{ display:'flex', alignItems:'center', paddingBottom:12, marginBottom:14, borderBottom:'1px solid var(--ds-border)' }}>
                  <div style={{ width:28, height:28, borderRadius:7, background:'var(--ds-hover)', display:'flex', alignItems:'center', justifyContent:'center', marginRight:10, flexShrink:0 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4D86E8" strokeWidth="1.7" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  </div>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:'var(--ds-text)' }}>{selectedPosition.positionName} — Resumes</div>
                    <div style={{ fontSize:11, color:'var(--ds-muted)', marginTop:1 }}>{selectedPosition.clientName} · {selectedPosition.jobCode}</div>
                  </div>
                </div>
                {loadingResumes ? (
                  <div style={{ textAlign:'center', padding:'30px 0' }}>
                    <Spinner animation="border" size="sm" style={{ color:'#4D86E8' }}/>
                    <p style={{ color:'var(--ds-muted)', fontSize:12, marginTop:8, marginBottom:0 }}>Loading resumes…</p>
                  </div>
                ) : resumes.length === 0 ? (
                  <div style={{ textAlign:'center', padding:'30px 0' }}>
                    <div style={{ width:48, height:48, borderRadius:12, background:'var(--ds-badge-active-bg)', border:'1px solid var(--ds-badge-active-border)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--ds-badge-active-text)" strokeWidth="1.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    </div>
                    <h6 style={{ color:'var(--ds-text)', marginBottom:6 }}>No Resumes Yet</h6>
                    <p style={{ color:'var(--ds-muted)', fontSize:12, marginBottom:12 }}>No resumes uploaded for this position.</p>
                    <button className="pt-ibtn primary" onClick={() => navigate(`/stack-ranking/${selectedPosition.positionId}`)}>+ Add Resumes</button>
                  </div>
                ) : (
                  <table className="pt-table" style={{ fontSize:12 }}>
                    <thead>
                      <tr>
                        {['Candidate','Email','Mobile','Added'].map(h => (
                          <th key={h} style={{ cursor:'default', fontSize:10, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--ds-muted)', fontWeight:600, padding:'8px 12px', borderBottom:'1px solid var(--ds-table-border)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {resumes.map(r => (
                        <tr key={r.resumeId} style={{ borderBottom:'1px solid var(--ds-table-border)' }}>
                          <td style={{ padding:'10px 12px', color:'var(--ds-text)', fontWeight:600 }}>{r.candidateName||'—'}</td>
                          <td style={{ padding:'10px 12px', color:'var(--ds-text2)' }}>{r.email||'—'}</td>
                          <td style={{ padding:'10px 12px', color:'var(--ds-text2)' }}>{r.mobile||'—'}</td>
                          <td style={{ padding:'10px 12px', color:'var(--ds-muted)', fontFamily:'monospace', fontSize:11 }}>{formatAppDate(r.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // TABLE MODE — exact reference design, theme-aware
  // ══════════════════════════════════════════════════════════════════════════
  const STATS = isCraftJD
    ? [
        { lbl:'Total Positions', val: statCounts.total,   color:'#0041C2', dot:null,      sub:'Across all clients',           filterVal:'all'     },
        { lbl:'Pending',         val: statCounts.pending, color:'#F59E0B', dot:'#F59E0B', sub:'Awaiting for Stack Ranking',   filterVal:'Pending' },
        { lbl:'Stacked',         val: statCounts.active,  color:'#10B981', dot:'#10B981', sub:'Ranking in progress',          filterVal:'Stacked' },
        { lbl:'Closed',          val: statCounts.closed,  color:'#6B7280', dot:'#6B7280', sub:'Position filled',              filterVal:'Close'   },
      ]
    : [
        { lbl:'Total Positions', val: statCounts.total,   color:'#0041C2', dot:null,      sub:'Across all clients',           filterVal:'all'     },
        { lbl:'Active',          val: statCounts.active,  color:'#10B981', dot:'#10B981', sub:'Ranking in progress',          filterVal:'Stacked' },
        { lbl:'In Review',       val: statCounts.pending, color:'#F59E0B', dot:'#F59E0B', sub:'Awaiting decision',            filterVal:'Pending' },
        { lbl:'Closed',          val: statCounts.closed,  color:'#6B7280', dot:'#6B7280', sub:'Position filled',              filterVal:'Close'   },
      ];

  return (
    <div className="pt-wrap">
      <style>{STYLES}</style>

      {/* ── Page heading ─────────────────────────────────────────────────── */}
      <div className="pt-page-head">
        <div>
          <h1 className="pt-page-title">{isCraftJD ? 'Craft JD' : 'Stack Ranking'}</h1>
          <p className="pt-page-tag">
            {isCraftJD
              ? 'Create and manage job descriptions with AI-powered assistance.'
              : 'Evaluate and rank candidates against job requirements using AI scoring.'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ds-text2)', cursor: 'pointer' }}
                 title="Positions you own or are assigned to">
            <input type="checkbox" checked={onlyMine} onChange={(e) => { setOnlyMine(e.target.checked); setCurrentPage(1); }} />
            Only positions I work on
          </label>
          {isCraftJD && newPositionDropdown}
        </div>
      </div>

      {/* ── Stat boxes (clickable filters) ──────────────────────────────── */}
      <div className="pt-stats">
        {STATS.map(s => {
          const isActive = s.filterVal === 'all' ? !statusFilter : statusFilter === s.filterVal;
          return (
            <div
              key={s.lbl}
              className="pt-stat"
              onClick={() => handleStatusFilterClick(s.filterVal)}
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                ...(isActive ? {
                  boxShadow: `0 0 0 2.5px ${s.color}, var(--ds-shadow)`,
                  transform: 'translateY(-2px)',
                } : {}),
              }}
              title={`Filter by ${s.lbl}`}
            >
              <div className="pt-stat-bar" style={{ background: s.color }} />
              <div className="pt-stat-lbl" style={{ display:'flex', alignItems:'center', gap:5 }}>
                {s.lbl}
                {isActive && (
                  <span style={{ display:'inline-block', width:6, height:6, borderRadius:'50%', background:s.color, flexShrink:0 }} />
                )}
              </div>
              <div className="pt-stat-val" style={{ color: s.color }}>{s.val}</div>
              <div className="pt-stat-sub">
                {s.dot && <span className="pt-stat-dot" style={{ background: s.dot }} />}
                {s.sub}
              </div>
            </div>
          );
        })}
      </div>

      {/* Active filter hint */}
      {statusFilter && (
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, marginTop:-6 }}>
          <span style={{ fontSize:11, color:'var(--ds-muted)' }}>Filtered by:</span>
          <span style={{ fontSize:11, fontWeight:700, color:'#1A5FD4', background:'var(--ds-active)', border:'1px solid var(--ds-border2)', borderRadius:20, padding:'2px 10px' }}>
            {STATS.find(s => s.filterVal === statusFilter)?.lbl || statusFilter}
          </span>
          <button onClick={() => handleStatusFilterClick('all')} style={{ fontSize:11, color:'var(--ds-muted)', background:'none', border:'none', cursor:'pointer', padding:0, textDecoration:'underline' }}>
            Clear
          </button>
        </div>
      )}

      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap', marginBottom:14 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <div className="pt-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ds-muted)" strokeWidth="1.6" style={{ flexShrink:0 }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              placeholder="Search role, client, or code…"
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <select className="pt-sel" value={statusFilter}
            onChange={e => handleStatusFilterClick(e.target.value || 'all')}>
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Stacked">Stacked</option>
            <option value="Close">Closed</option>
          </select>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <button className="pt-ibtn" onClick={exportCSV}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export CSV
          </button>
        </div>
      </div>

      {/* ── Table panel ──────────────────────────────────────────────────── */}
      <div className="pt-panel">
        <div className="pt-panel-head">
          <div>
            <h2>{isCraftJD ? 'Craft JD — Positions' : 'Job Positions'}</h2>
            <p>
              {isCraftJD
                ? 'Click a row to view resumes · Click a header to sort'
                : 'Click a row to view ranking results · Click a header to sort'}
            </p>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            {isCraftJD && analyticsFocusIds?.size > 0 && (
              <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:'rgba(0,65,194,0.1)', border:'1px solid rgba(0,65,194,0.35)', borderRadius:20, padding:'3px 10px', fontSize:11, color:'#0041C2', fontWeight:600 }}>
                From JD Analytics{analyticsFocusLabel ? `: ${analyticsFocusLabel}` : ''}
                <button type="button" title="Show all positions" onClick={clearAnalyticsFocus} style={{ background:'none', border:'none', padding:0, cursor:'pointer', color:'#0041C2', lineHeight:1, fontSize:13 }}>×</button>
              </span>
            )}
            {dateFilter && (
              <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:'var(--ds-hover)', border:'1px solid var(--ds-border2)', borderRadius:20, padding:'3px 10px', fontSize:11, color:'#1A5FD4', fontWeight:600 }}>
                {formatAppDateFromKey(dateFilter)}
                <button type="button" title="Clear date filter" onClick={() => { setDateFilter(''); setCurrentPage(1); }} style={{ background:'none', border:'none', padding:0, cursor:'pointer', color:'#1A5FD4', lineHeight:1, fontSize:13 }}>×</button>
              </span>
            )}
            <span className="pt-results">{sortedPositions.length} result{sortedPositions.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        <div className="pt-table-wrap">
          {positions.length === 0 ? (
            <div className="pt-empty">
              <div className="pt-empty-ico">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
              </div>
              <p>{isCraftJD ? 'No positions yet. Create your first to get started.' : 'No positions have been created yet.'}</p>
              {isCraftJD && (
                <div style={{ margin: '12px auto 0', display: 'flex', justifyContent: 'center' }}>
                  {newPositionDropdown}
                </div>
              )}
            </div>
          ) : (
            <table className={`pt-table${isCraftJD ? ' pt-table-craft' : ' pt-table-stack'}`}>
              {isCraftJD ? (
                <colgroup>
                  <col style={{ width: '48px' }} />
                  <col className="pt-col-job-role" />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '110px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '120px' }} />
                </colgroup>
              ) : (
                <colgroup>
                  <col className="pt-col-sno" style={{ width: '3rem' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '15%' }} />
                </colgroup>
              )}
              <thead>
                <tr>
                  {/* S.No */}
                  <th
                    className={`pt-col-sno${sortColumn==='sno'?' sorted':''}`}
                    onClick={() => handleSort('sno')}
                    title={!isCraftJD ? 'Serial number' : undefined}
                  >
                    {isCraftJD ? <>S.No {sortIco('sno')}</> : 'S.N'}
                  </th>
                  {/* Job Role */}
                  <th className={sortColumn==='jobRole'?'sorted':''} onClick={() => handleSort('jobRole')}>
                    Job Role {sortIco('jobRole')}
                  </th>
                  {/* Client */}
                  <th className={sortColumn==='clientName'?'sorted':''} onClick={() => handleSort('clientName')}>
                    {isCraftJD ? 'Client Name' : 'Client'} {sortIco('clientName')}
                  </th>
                  {/* Job Code */}
                  <th className={sortColumn==='jobCode'?'sorted':''} onClick={() => handleSort('jobCode')}>
                    Job Code {sortIco('jobCode')}
                  </th>
                  {/* Created On — sort + calendar */}
                  <th className={`pt-th-created-on${sortColumn==='createdOn'?' sorted':''}${!isCraftJD ? ' pt-th-created' : ''}`}>
                    <div className="pt-th-created-on-head">
                      <span className="pt-th-created-on-label" onClick={() => handleSort('createdOn')}>
                        {isCraftJD ? 'Created On' : 'Created'}
                      </span>
                      <span className="pt-th-created-on-sort" onClick={() => handleSort('createdOn')}>
                        {sortIco('createdOn')}
                      </span>
                      <div ref={datePickerRef} className="pt-th-date-filter">
                        <button
                          type="button"
                          className={`pt-th-date-filter-btn${dateFilter ? ' is-active' : ''}`}
                          title="Filter by date"
                          onClick={e => { e.stopPropagation(); setShowDatePicker(s => !s); }}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                        </button>
                        {showDatePicker && (
                          <div className="pt-datepop" onClick={e => e.stopPropagation()}>
                            <label htmlFor="pt-created-date-filter">Filter by date</label>
                            <div className="pt-datepop-fields">
                              <div className="pt-datepop-row">
                                <input
                                  id="pt-created-date-filter"
                                  type="date"
                                  value={dateFilter}
                                  max={maxCreatedDate}
                                  onChange={e => handleCreatedDateFilterChange(e.target.value)}
                                  onBlur={e => handleCreatedDateFilterChange(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') handleCreatedDateFilterChange(e.currentTarget.value); }}
                                />
                                <button
                                  type="button"
                                  className="pt-clr"
                                  disabled={!dateFilter}
                                  title="Clear date filter"
                                  onClick={() => { setDateFilter(''); setShowDatePicker(false); setCurrentPage(1); }}
                                >
                                  Clear date filter
                                </button>
                              </div>
                              {dateFilter && (
                                <span className="pt-datepop-selected">{formatAppDateFromKey(dateFilter)}</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  {/* Status */}
                  <th className={`pt-th-status${sortColumn==='status'?' sorted':''}`} onClick={() => handleSort('status')}>
                    Status {sortIco('status')}
                  </th>
                  {/* Created By */}
                  <th title={!isCraftJD ? 'Created By' : undefined}>
                    {isCraftJD ? 'Created By' : 'Creator'}
                  </th>
                  {/* Ranked Resumes — stackRanking only */}
                  {!isCraftJD && <th style={{ width:130, textAlign:'center' }}>Ranked Resumes</th>}
                  {/* Actions */}
                  <th style={{ width:148, textAlign:'center' }}>Actions</th>
                  {!isCraftJD && <th className="pt-th-ranked" style={{ textAlign:'center' }} title="Ranked Resumes">Ranked</th>}
                  {/* Actions — stackRanking only */}
                  {!isCraftJD && <th className="pt-col-actions" style={{ textAlign:'center', width:148 }}>Actions</th>}
                </tr>
              </thead>
              <tbody key={`grid-${statusFilter || 'all'}-${dateFilter}-${searchTerm}-${analyticsFocusIds ? [...analyticsFocusIds].join(',') : 'all'}`}>
                {sortedPositions.length === 0 ? (
                  <tr>
                    <td colSpan={isCraftJD ? craftColCount : 9}>
                      <div className="pt-empty">
                        <div className="pt-empty-ico">
                          <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        </div>
                        <p>No positions match your filters.<br/>Try adjusting your search or filter criteria.</p>
                        {isCraftJD && analyticsFocusIds?.size > 0 && (
                          <button
                            type="button"
                            onClick={clearAnalyticsFocus}
                            style={{
                              marginTop: 10, background: 'rgba(0,65,194,0.1)', border: '1px solid rgba(0,65,194,0.35)',
                              borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#0041C2', cursor: 'pointer',
                            }}
                          >
                            Show all positions
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : currentRecords.map((pos, idx) => {
                  const sc = getSC(positionStatusKey(pos));
                  const cc = getCC(pos.clientName);
                  const isAnalyticsFocus = analyticsFocusIds?.has(String(pos.positionId));
                  return (
                    <tr
                      key={pos.positionId}
                      className={isCraftJD ? 'click' : ''}
                      onClick={isCraftJD ? () => handleRowClick(pos) : undefined}
                      style={isAnalyticsFocus ? { background: 'rgba(0,65,194,0.06)' } : undefined}
                    >
                      {/* S.No */}
                      <td className={!isCraftJD ? 'pt-col-sno' : undefined}>
                        <span className={!isCraftJD ? 'pt-sno-val' : undefined} style={{ color:'var(--ds-text)', fontWeight:800, fontSize: isCraftJD ? 13 : undefined }}>
                          {getStaticSno(pos.positionId)}
                        </span>
                      </td>
                      {/* Job Role */}
                      <td>
                        <div className="pt-role-cell">
                          <div className="pt-role-ico">
                            <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
                          </div>
                          <div className="pt-role-name" title={pos.positionName || ''}>{pos.positionName || '—'}</div>
                          <PositionBadges position={pos} />
                        </div>
                      </td>
                      {/* Client */}
                      <td>
                        <div className="pt-client-cell" style={{ minWidth: 0 }}>
                          <div className="pt-client-av" style={{ background:`${cc}18`, border:`1px solid ${cc}35` }}>
                            <span style={{ color:cc }}>{getIni(pos.clientName)}</span>
                          </div>
                          <span className="pt-client-name pt-ellipsis" title={pos.clientName || ''}>{pos.clientName || '—'}</span>
                        </div>
                      </td>
                      {/* Job Code */}
                      <td><span className="pt-code" title={pos.jobCode || ''}>{pos.jobCode || 'N/A'}</span></td>
                      {/* Created On */}
                      <td><span className="pt-date">{formatAppDate(pos.createdAt)}</span></td>
                      {/* Status */}
                      <td className="pt-cell-status">
                        <span
                          className={`pt-badge ${sc.cls}`}
                          title={
                            positionStatusKey(pos) === 'Close' && (pos.closeReason || pos.close_reason)
                              ? `Closed: ${pos.closeReason || pos.close_reason}`
                              : sc.label
                          }
                        >
                          <span className="pt-badge-dot" aria-hidden="true"/>
                          <span className="pt-badge-label">{formatPositionStatusLabel(pos)}</span>
                        </span>
                      </td>
                      {/* Created By */}
                      <td>
                        {(() => {
                          const name = pos.createdByName || pos.createdBy || createdByMap[pos.positionId] || '';
                          if (!name) return <span style={{ color: 'var(--ds-muted)', fontSize: 12 }}>—</span>;
                          return (
                            <div className="pt-created-by" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ width: 22, height: 22, borderRadius: 6, background: 'rgba(0,65,194,0.12)', border: '1px solid rgba(0,65,194,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#4D86E8" strokeWidth="2" strokeLinecap="round">
                                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                                </svg>
                              </div>
                              <span className="pt-ellipsis" style={{ fontSize: 11, color: 'var(--ds-text)', fontWeight: 600, flex: 1 }} title={name}>{name}</span>
                            </div>
                          );
                        })()}
                      </td>
                      {/* Ranked Resumes */}
                      {!isCraftJD && (
                        <td style={{ textAlign:'center' }}>
                          <span style={{ background:'var(--ds-hover)', border:'1px solid var(--ds-border2)', borderRadius:6, padding:'2px 8px', fontSize:11, fontWeight:700, color:'#4D86E8', display:'inline-block' }}>
                            {resumeCounts[pos.positionId] || 0}
                          </span>
                        </td>
                      )}
                      {/* Actions */}
                      <td onClick={e => e.stopPropagation()} style={{ textAlign:'center' }}>
                        {renderActionButtons(pos)}
                      </td>
                      {!isCraftJD && (
                        <td className="pt-col-actions" onClick={e => e.stopPropagation()} style={{ textAlign:'center' }}>
                          {renderActionButtons(pos)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {positions.length > 0 && renderPagination()}
      </div>

      {assignTarget && (
        <AssignPositionModal
          position={assignTarget}
          onClose={() => setAssignTarget(null)}
          onSaved={() => fetchPositions({ silent: true })}
        />
      )}
      {transferTarget && (
        <TransferPositionModal
          position={transferTarget}
          onClose={() => setTransferTarget(null)}
          onSaved={() => fetchPositions({ silent: true })}
        />
      )}

      {/* New Position modal */}
      <NewPositionModal
        show={showNewPositionModal}
        onHide={() => setShowNewPositionModal(false)}
        onSuccess={handlePositionCreated}
        source={newPositionSource}
        currentUser={currentUser}
      />

      {/* Delete modal */}
      {showDeleteModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleteLoading) {
              setShowDeleteModal(false);
              setDeletingPosition(null);
            }
          }}
        >
          <div style={{
            background: 'var(--ds-card)', borderRadius: 12, width: '100%', maxWidth: 440,
            boxShadow: 'var(--ds-shadow-md)', border: '1px solid var(--ds-border)',
            fontFamily: "'Plus Jakarta Sans',sans-serif",
          }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h6 style={{ margin: 0, fontWeight: 700, color: 'var(--ds-text)', fontSize: 15 }}>Confirm Delete</h6>
              <button
                type="button"
                disabled={deleteLoading}
                onClick={() => { setShowDeleteModal(false); setDeletingPosition(null); }}
                style={{
                  background: 'none', border: 'none', cursor: deleteLoading ? 'not-allowed' : 'pointer',
                  color: 'var(--ds-muted)', fontSize: 20, lineHeight: 1, opacity: deleteLoading ? 0.5 : 1,
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: 'rgba(239,68,68,0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: '0 0 12px', color: 'var(--ds-text)', fontSize: 13, lineHeight: 1.5 }}>
                    Permanently delete this position and all associated JDs and data?
                  </p>
                  <div style={{
                    background: 'var(--ds-surface)', border: '1px solid var(--ds-border)',
                    borderRadius: 10, padding: '12px 14px',
                  }}>
                    <div style={{ fontWeight: 700, color: 'var(--ds-text)', fontSize: 14 }}>
                      {deletingPosition?.positionName || 'Position'}
                    </div>
                    {deletingPosition?.clientName ? (
                      <div style={{ color: 'var(--ds-muted)', fontSize: 12, marginTop: 4 }}>{deletingPosition.clientName}</div>
                    ) : null}
                    {deletingPosition?.jobCode ? (
                      <div style={{ color: 'var(--ds-muted)', fontSize: 11, marginTop: 2 }}>ID: {deletingPosition.jobCode}</div>
                    ) : null}
                  </div>
                  <p style={{ margin: '12px 0 0', color: 'var(--ds-muted)', fontSize: 12 }}>This action cannot be undone.</p>
                </div>
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                disabled={deleteLoading}
                onClick={() => { setShowDeleteModal(false); setDeletingPosition(null); }}
                style={{
                  borderRadius: 8, padding: '8px 16px', border: '1.5px solid var(--ds-border)',
                  background: 'transparent', color: 'var(--ds-text)', fontFamily: 'inherit',
                  cursor: deleteLoading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
                  opacity: deleteLoading ? 0.6 : 1, transition: 'all 0.18s',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleteLoading}
                onClick={performDelete}
                style={{
                  borderRadius: 8, padding: '8px 16px', background: '#dc3545', border: 'none',
                  color: '#fff', fontFamily: 'inherit', cursor: deleteLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6,
                  boxShadow: '0 2px 8px rgba(220,53,69,0.25)', opacity: deleteLoading ? 0.7 : 1,
                  transition: 'all 0.18s',
                }}
              >
                {deleteLoading ? <><Spinner size="sm" animation="border" /> Deleting…</> : 'Delete Position'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close position modal */}
      {showCloseModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !closeLoading) {
              resetCloseModal();
            }
          }}
        >
          <div style={{
            background: 'var(--ds-card)', borderRadius: 12, width: '100%', maxWidth: 480,
            boxShadow: 'var(--ds-shadow-md)', border: '1px solid var(--ds-border)',
            fontFamily: "'Plus Jakarta Sans',sans-serif",
          }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h6 style={{ margin: 0, fontWeight: 700, color: 'var(--ds-text)', fontSize: 15 }}>Confirm Close</h6>
              <button
                type="button"
                disabled={closeLoading}
                onClick={resetCloseModal}
                style={{
                  background: 'none', border: 'none', cursor: closeLoading ? 'not-allowed' : 'pointer',
                  color: 'var(--ds-muted)', fontSize: 20, lineHeight: 1, opacity: closeLoading ? 0.5 : 1,
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: 'rgba(245,158,11,0.14)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M9 12l2 2 4-4"/>
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: '0 0 12px', color: 'var(--ds-text)', fontSize: 13, lineHeight: 1.5 }}>
                    Close this position? It will move to the Closed tile immediately.
                  </p>
                  <div style={{
                    background: 'var(--ds-surface)', border: '1px solid var(--ds-border)',
                    borderRadius: 10, padding: '12px 14px',
                  }}>
                    <div style={{ fontWeight: 700, color: 'var(--ds-text)', fontSize: 14 }}>
                      {closingPosition?.positionName || 'Position'}
                    </div>
                    {closingPosition?.clientName ? (
                      <div style={{ color: 'var(--ds-muted)', fontSize: 12, marginTop: 4 }}>{closingPosition.clientName}</div>
                    ) : null}
                    {closingPosition?.jobCode ? (
                      <div style={{ color: 'var(--ds-muted)', fontSize: 11, marginTop: 2 }}>ID: {closingPosition.jobCode}</div>
                    ) : null}
                  </div>
                  <label style={{ display: 'block', marginTop: 14, fontSize: 12, fontWeight: 700, color: 'var(--ds-text)' }}>
                    Comment / reason <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {CLOSE_REASON_PRESETS.map((preset) => {
                      const active = closeReason === preset;
                      return (
                        <button
                          key={preset}
                          type="button"
                          disabled={closeLoading}
                          onClick={() => setCloseReason(preset)}
                          style={{
                            borderRadius: 999, padding: '4px 10px', fontSize: 11, fontWeight: 600,
                            border: active ? '1px solid #f59e0b' : '1px solid var(--ds-border)',
                            background: active ? 'rgba(245,158,11,0.12)' : 'transparent',
                            color: active ? '#b45309' : 'var(--ds-text)',
                            cursor: closeLoading ? 'not-allowed' : 'pointer',
                            fontFamily: 'inherit',
                          }}
                        >
                          {preset}
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    value={closeReason}
                    disabled={closeLoading}
                    maxLength={CLOSE_REASON_MAX}
                    onChange={(e) => setCloseReason(e.target.value)}
                    placeholder="Why is this position being closed?"
                    style={{
                      width: '100%', marginTop: 8, minHeight: 84, resize: 'vertical',
                      borderRadius: 8, border: '1px solid var(--ds-border)', padding: '8px 10px',
                      fontFamily: 'inherit', fontSize: 13, color: 'var(--ds-text)',
                      background: 'var(--ds-card)', boxSizing: 'border-box', outline: 'none',
                    }}
                  />
                  <div style={{ marginTop: 4, fontSize: 11, color: 'var(--ds-muted)', textAlign: 'right' }}>
                    {closeReason.trim().length}/{CLOSE_REASON_MAX}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                disabled={closeLoading}
                onClick={resetCloseModal}
                style={{
                  borderRadius: 8, padding: '8px 16px', border: '1.5px solid var(--ds-border)',
                  background: 'transparent', color: 'var(--ds-text)', fontFamily: 'inherit',
                  cursor: closeLoading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
                  opacity: closeLoading ? 0.6 : 1, transition: 'all 0.18s',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={closeLoading || !closeReason.trim()}
                onClick={performClose}
                style={{
                  borderRadius: 8, padding: '8px 16px', background: '#f59e0b', border: 'none',
                  color: '#fff', fontFamily: 'inherit',
                  cursor: (closeLoading || !closeReason.trim()) ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6,
                  boxShadow: '0 2px 8px rgba(245,158,11,0.28)',
                  opacity: (closeLoading || !closeReason.trim()) ? 0.7 : 1,
                  transition: 'all 0.18s',
                }}
              >
                {closeLoading ? <><Spinner size="sm" animation="border" /> Closing…</> : 'Close Position'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
